<!--
  FrameX Frames-list row: renders one frame (collection location + its item)
  and reports clicks with modifier keys; FrameMarkersList owns the selection
  store. Dot: no-scale (red) > built (blue) > unbuilt (hollow).
-->
<script>
  import { createEventDispatcher } from "svelte";

  import formatDims from "@local/extensions/formatters/format-dims.js";
  import { isFrameBuilt } from "../derive.js";

  /** The frame's location (`record_type === "collection"`). */
  export let loc;
  /** The frame's record — `$group.items[loc.record_id]`. */
  export let item;
  export let settings;
  export let selected = false;
  /** Show the frame's Scope on the right — used in "Group by Nothing" mode,
      where scope isn't already carried by a group header. */
  export let showScope = false;
  /** Styling-only flag for rows in the "Not in FrameX" section — dims the row.
      Orphans are ordinary placements, so behaviour is otherwise unchanged. */
  export let orphan = false;

  const dispatch = createEventDispatcher();

  let rowEl;
  let wasSelected = false;
  // Bring a newly-selected row into view (e.g. after a canvas click) when it's
  // scrolled out of the list. block:"nearest" scrolls the minimum needed, so a
  // row that's already visible — like one just clicked in the list — doesn't
  // jump, and it only fires on the false→true transition, not every re-render.
  $: {
    if (selected && !wasSelected && rowEl) rowEl.scrollIntoView({ block: "nearest" });
    wasSelected = selected;
  }

  $: scope = item?.scope || "";
  $: hasScale = Boolean(loc.clip?.drawing_scale);
  // The dot fills blue once the frame is built — has a width and height, whether
  // analyzer- or hand-entered. No-scale still takes precedence (red ring).
  $: dotState = !hasScale ? "no-scale" : isFrameBuilt(item) ? "analyzed" : "unanalyzed";
  // Show width × height whenever the frame carries both, exactly as the
  // legacy list's `description()` does — not gated on "analyzed", which left
  // built-but-not-flagged frames showing a bare hyphen.
  $: dims = item?.width && item?.height ? formatDims(item, settings) : "";
  $: mark = item?.mark || "—";
  $: qty = item?.quantity ?? 0;

  function handleClick(event) {
    dispatch("select", {
      loc,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
    });
  }

  // Double-click zooms the canvas to the extents of the sheet the frame is on,
  // matching the legacy frames list. `zoom-to-page` carries a page id and is
  // forwarded up to Document.svelte's existing zoomToPage handler.
  function handleDblClick() {
    dispatch("zoom-to-page", { page: loc.page_id });
  }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  bind:this={rowEl}
  class="fx-marker-row"
  class:selected
  class:orphan
  on:click={handleClick}
  on:dblclick={handleDblClick}
  on:mouseenter={() => loc && dispatch("hover", { loc })}
  on:mouseleave={() => dispatch("unhover")}>
  <span class="fx-marker-dot fx-dot-{dotState}" />
  <span class="fx-marker-mark">{mark}</span>
  <span class="fx-marker-dims">{dims}</span>
  {#if showScope && scope}
    <span class="fx-marker-scope">{scope}</span>
  {/if}
  <span class="fx-marker-qty">{qty}</span>
</div>

<style lang="scss">
  // Matches the legacy frames list row: text-xs (12px / 1rem line-height),
  // py-1 density, gap-2. Selection is a light blue field matching the Pages
  // tab's selected row — light enough that the dot and muted spans stay legible.
  .fx-marker-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 16px;
    font-size: 12px;
    line-height: 1rem;
    color: var(--fx-text);
    cursor: pointer;

    &:hover {
      background: #f7f8fa;
    }

    // Light blue field, the same token the Pages tab's selected row uses. No
    // stroke: the Pages tab carries its blue border on the page-number tile,
    // and a frame row has no equivalent tile to carry one.
    &.selected {
      background: var(--fx-accent-tint);
    }

    // "Not in FrameX" rows read as a lesser, recoverable list — muted, but
    // otherwise behave exactly like working-set rows.
    &.orphan {
      color: var(--fx-text-secondary);
    }
  }

  .fx-marker-dot {
    width: 7px;
    height: 7px;
    flex: none;
    border-radius: 50%;
    background: #fff;
    border: 1px solid var(--fx-dot-unanalyzed);

    &.fx-dot-analyzed {
      background: var(--fx-accent);
      border-color: var(--fx-accent);
    }

    // No scale takes precedence over the analyzed state.
    &.fx-dot-no-scale {
      background: #fff;
      border-color: var(--fx-danger);
    }
  }

  .fx-marker-mark {
    flex: none;
  }

  .fx-marker-dims {
    flex: 1;
    min-width: 0;
    color: var(--fx-text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Frame Scope, shown on the right (before Qty) only in "Group by Nothing"
  // mode. Muted and truncated so a long scope never crowds out the mark/dims.
  .fx-marker-scope {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 45%;
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--fx-text-secondary);
  }

  // Right-aligned quantity, sharing the "Qty" heading's column width so the
  // numbers stack directly under it.
  .fx-marker-qty {
    flex: none;
    min-width: 2.25rem;
    text-align: right;
    color: var(--fx-text-secondary);
    font-variant-numeric: tabular-nums;
  }
</style>
